import React, { useEffect, useRef, useState, memo } from 'react';
import Chart from 'chart.js/auto';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import useChartImageDownload from './useChartImageDownload';
import { useTheme } from '@mui/material/styles';
import OverallTheme from '../Styles/OverallTheme'
const DynamicStackColumnChart = ({ chartId, data, variableOne, variableTwo }) => {
  const canvasRef = useRef(null);
  const { chartRef, downloadImage } = useChartImageDownload();
  const [isChartInitialized, setIsChartInitialized] = useState(false);

  const secondaryColors = Object.values(OverallTheme.palette.REAL.secondary);
  const usedColors = new Set();

  const updateChartSize = () => {
    const parentWidth = canvasRef.current.parentElement.clientWidth;
    const parentHeight = canvasRef.current.parentElement.clientHeight;
    canvasRef.current.width = parentWidth;
    canvasRef.current.height = parentHeight;
    if (chartRef.current) {
      chartRef.current.resize();
    }
  };

  useEffect(() => {
    const initializeChart = () => {
      const categories = [...new Set(data.map((item) => item[variableOne]))].sort();
  const labels = [...new Set(data.map((item) => item[variableTwo]))];

  // Calculate total values for each label
  const labelTotals = labels.map((label) => ({
    label,
    total: data
      .filter((item) => item[variableTwo] === label)
      .reduce((sum, item) => sum + item.Total, 0),
  }));

  // Sort labels based on total values in descending order
  const sortedLabels = labelTotals
    .sort((a, b) => b.total - a.total)
    .map((item) => item.label);

  // Create datasets based on sorted labels
  const groupData = sortedLabels.map((label, index) => {
    let backgroundColor = secondaryColors[index];
    let attemptCount = 0;
    while (usedColors.has(backgroundColor) && attemptCount < secondaryColors.length) {
      attemptCount++;
      backgroundColor = secondaryColors[(index + attemptCount) % secondaryColors.length];
    }
    usedColors.add(backgroundColor);
    return {
      label,
      data: categories.map(
        (category) => data.find((item) => item[variableOne] === category && item[variableTwo] === label)?.Total || 0
      ),
      backgroundColor,
    };
  });

  const total = data.reduce((acc, curr) => acc + curr.Total, 0);
      const chartData = {
        labels: categories,
        datasets: groupData,
      };

      const chartOptions = {
        scales: {
          x: { stacked: true },
          y: { stacked: true },
        },
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (context) => {
                const datasetLabel = context.dataset.label;
                const Total = context.raw;
                const percentage = ((Total / total) * 100).toFixed(2);
                return `${datasetLabel}: ${percentage}% | (${Total.toLocaleString()})`;
              },
            },
          },
        },
      };

      if (chartRef.current) {
        chartRef.current.destroy();
      }

      chartRef.current = new Chart(canvasRef.current, {
        type: 'bar',
        data: chartData,
        options: chartOptions,
        plugins: [{ id: 'chartInitialized', afterInit: () => setIsChartInitialized(true) }],
      });
    };

    initializeChart();
    updateChartSize();

    const handleResize = () => {
      updateChartSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, variableOne, variableTwo, chartRef, secondaryColors, usedColors]);

  const handleDownload = () => downloadImage(chartId);

  return (
    <div style={{ position: 'relative', width: '100%', height: '80%' }}>
      <div style={{ width: '100%', height: '100%' }}>
        <canvas ref={canvasRef} width="100%" height="100%" style={{ width: '100%', height: '100%' }}></canvas>
      </div>

    </div>
  );
};

DynamicStackColumnChart.propTypes = {
  chartId: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      [PropTypes.string]: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      Total: PropTypes.number.isRequired,
    })
  ).isRequired,
  variableOne: PropTypes.string.isRequired,
  variableTwo: PropTypes.string.isRequired,
};

export default memo(DynamicStackColumnChart);
